/** Small shared formatters for the mission panels. */

export function formatWhen(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  return formatAgeMinutes((Date.now() - then) / 60000);
}

/**
 * Age already computed on the pin, so a stolen or timezone-skewed
 * timestamp cannot read as "just now" while the fix is months old.
 */
export function formatAgeMinutes(ageMinutes: number): string {
  if (!Number.isFinite(ageMinutes)) return '';
  const minutes = Math.max(0, Math.round(ageMinutes));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(minutes / 1440);
  return days < 60 ? `${days}d ago` : `${Math.round(days / 30)}mo ago`;
}

const FEET_PER_M = 3.28084;
const M_PER_MILE = 1609.344;

/**
 * Distance in the units an operator thinks in.
 *
 * Imperial by default, to match `formatSpeed` — a panel that reads "42 mph"
 * beside "4.4 km" makes the reader do a conversion mid-run to compare two
 * numbers about the same vehicle.
 *
 * The small unit is feet rather than fractions of a mile, and switches at a
 * thousand of them for the same reason the metric side switches at a thousand
 * metres. A deviation threshold reads as "820 ft", which is a distance someone
 * can picture; "0.2 mi" is the same fact in a form nobody paces out.
 */
export function formatMeters(meters?: number | null, units: 'km' | 'mi' = 'mi'): string {
  if (meters == null || !Number.isFinite(meters)) return '';
  if (units === 'mi') {
    const feet = meters * FEET_PER_M;
    if (feet < 1000) return `${Math.round(feet)} ft`;
    const miles = meters / M_PER_MILE;
    return `${miles.toFixed(miles < 10 ? 1 : 0)} mi`;
  }
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(meters < 10000 ? 1 : 0)} km`;
}

/** Clock time, for an arrival an operator will compare against their watch. */
export function formatClock(iso?: string | null): string {
  if (!iso) return '—';
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return '—';
  return when.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/** Compact elapsed or remaining time: "4m", "1h 12m". */
export function formatSpan(seconds?: number | null): string {
  if (seconds == null || !Number.isFinite(seconds)) return '—';
  const total = Math.max(0, Math.round(seconds / 60));
  if (total < 60) return `${total}m`;
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
}

/**
 * Speed in the units an operator thinks in.
 *
 * Metres per second is what the maths produces and what nobody reads a dial in,
 * so it is converted once here rather than at each call site.
 */
export function formatSpeed(mps?: number | null, units: 'km' | 'mi' = 'mi'): string {
  if (mps == null || !Number.isFinite(mps)) return '—';
  return units === 'mi'
    ? `${Math.round(mps * 2.23694)} mph`
    : `${Math.round(mps * 3.6)} km/h`;
}

/**
 * A signed difference against a reference, in words.
 *
 * The sign is spelled out rather than shown as a minus, because "-6m" against a
 * previous run is genuinely ambiguous about which way is good.
 */
export function formatDelta(seconds?: number | null): string {
  if (seconds == null || !Number.isFinite(seconds)) return '—';
  if (Math.abs(seconds) < 60) return 'level';
  return seconds > 0
    ? `${formatSpan(seconds)} slower`
    : `${formatSpan(-seconds)} faster`;
}
